import throttle from 'lodash/throttle';

import {
	KnowledgeDiscoverySearchChatIntentDetectionModelVersion,
	type KnowledgeDiscoverySearchChatIntentDetectionModelVersionType,
} from '@atlassian/search-client/intent-detection';

import { CONVERGENCE_PRIMARY_PRODUCTS } from './constants';
import { getInputIntentLocal } from './get-input-intent-local';
import { getInputIntentRemote } from './get-input-intent-remote';
import {
	type ConvergencePrimaryProduct,
	type IntentContext,
	type IntentModel,
	type IntentResponse,
	type MultilayerIntentResponse,
	type ObserveRemoteIntentRequest,
	type PrimaryProduct,
	type RemoteIntentRequestHandlers,
} from './types';

/**
 * Safe default intent response when error handling is needed.
 *
 * Returns a no-match response (FPS routing) that is safe for callers to use.
 * This ensures that errors in local or remote classification do not break the
 * user experience — the input is simply passed to full-page search.
 */
const createErrorFallbackIntent = (): IntentResponse => ({
	didMatch: false,
	type: 'FPS',
	pattern: 'NO_PATTERN',
});

type MultilayerIntentContext = IntentContext & Required<Pick<IntentContext, 'locale'>>;

type MultilayerIntentOptions = {
	/** Optional lifecycle observer called only when classification reaches the remote engine. */
	observeRemoteRequest?: ObserveRemoteIntentRequest;
	/** Cancels remote intent detection while it is waiting to be scheduled. */
	signal?: AbortSignal;
	/** Decides when an eligible remote request may start. Defaults to immediate execution. */
	scheduleRemoteIntent?: RemoteIntentScheduler;
};

export const REMOTE_INTENT_THROTTLE_MS = 250;

export type RemoteIntentScheduler = {
	(): Promise<boolean>;
	cancel: () => void;
};

/**
 * Creates a per-consumer Lodash scheduler that runs remote intent detection on the leading edge.
 * Calls during each throttle interval are coalesced into one trailing execution for the latest
 * input, so continuous typing is classified remotely at most once every 250 ms.
 */
export const createLeadingAndTrailingRemoteIntentScheduler = (): RemoteIntentScheduler => {
	let pendingResolve: ((shouldExecute: boolean) => void) | undefined;

	const resolvePending = (shouldExecute: boolean) => {
		const resolve = pendingResolve;
		pendingResolve = undefined;
		resolve?.(shouldExecute);
	};

	const trigger = throttle(() => resolvePending(true), REMOTE_INTENT_THROTTLE_MS, {
		leading: true,
		trailing: true,
	});

	const schedule = (): Promise<boolean> => {
		resolvePending(false);

		return new Promise((resolve) => {
			pendingResolve = resolve;
			trigger();
		});
	};

	schedule.cancel = () => {
		trigger.cancel();
		resolvePending(false);
	};

	return schedule;
};

const isConvergencePrimaryProduct = (
	primaryProduct: PrimaryProduct | undefined,
): primaryProduct is ConvergencePrimaryProduct => {
	const supportedProducts: readonly PrimaryProduct[] = CONVERGENCE_PRIMARY_PRODUCTS;

	return primaryProduct !== undefined && supportedProducts.includes(primaryProduct);
};

const isConvergenceIntentModel = (
	model: IntentModel | undefined,
): model is KnowledgeDiscoverySearchChatIntentDetectionModelVersionType =>
	Object.values(KnowledgeDiscoverySearchChatIntentDetectionModelVersion).some(
		(candidate) => candidate === model,
	);

const resolveConvergenceIntentModel = (
	model: IntentModel | undefined,
): KnowledgeDiscoverySearchChatIntentDetectionModelVersionType =>
	isConvergenceIntentModel(model)
		? model
		: KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1;

const safelyNotifyObserver = <T>(notify: () => T): T | undefined => {
	try {
		return notify();
	} catch {
		return undefined;
	}
};

/**
 * Classifies a raw input string across the available intent detection layers.
 *
 * This is the single orchestration boundary for local and remote intent
 * detection. It always returns a promise, decides whether a remote layer is
 * needed, consults the supplied remote scheduler, and owns error handling,
 * fallback, and layer provenance. Remote engine selection and timeout handling
 * are delegated to `getInputIntentRemote`.
 *
 * If local or remote intent detection throws an error, returns a safe
 * fallback (FPS/NO_PATTERN) rather than rejecting the promise. This ensures
 * errors do not propagate to the caller and the user experience remains intact.
 */
export const getInputIntentMultilayer = async (
	input: string,
	context: MultilayerIntentContext,
	options: MultilayerIntentOptions = {},
): Promise<MultilayerIntentResponse> => {
	let localIntent: IntentResponse;

	try {
		localIntent = getInputIntentLocal(input, context);
	} catch {
		return {
			intent: createErrorFallbackIntent(),
			source: 'local',
		};
	}

	if (
		localIntent.didMatch ||
		!isConvergencePrimaryProduct(context.primaryProduct) ||
		!context.cloudId
	) {
		return {
			intent: localIntent,
			source: 'local',
		};
	}
	const shouldRunRemote =
		!options.signal?.aborted &&
		(!options.scheduleRemoteIntent || (await options.scheduleRemoteIntent())) &&
		!options.signal?.aborted;

	if (!shouldRunRemote) {
		return {
			intent: localIntent,
			source: 'local',
		};
	}

	const requestObserver: RemoteIntentRequestHandlers | undefined = safelyNotifyObserver(() =>
		options.observeRemoteRequest?.(input),
	);

	try {
		const remoteIntent = await getInputIntentRemote(input, {
			primaryProduct: context.primaryProduct,
			cloudId: context.cloudId,
			locale: context.locale,
			model: resolveConvergenceIntentModel(context.model),
			...(requestObserver?.onNetworkRequest && {
				onNetworkRequest: () => safelyNotifyObserver(() => requestObserver.onNetworkRequest?.()),
			}),
		});

		safelyNotifyObserver(() => requestObserver?.onSuccess(remoteIntent));

		return {
			intent: remoteIntent,
			source: 'remote',
		};
	} catch (error) {
		safelyNotifyObserver(() => requestObserver?.onFailure(error));

		return {
			intent: localIntent,
			source: 'local',
		};
	}
};
